import { useState, useEffect, useCallback } from "react";
import apiClient from "../api/client";

const CACHE_KEY = "favorites_cache";

export const useFavorites = () => {
  const userId = sessionStorage.getItem("userId");

  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(CACHE_KEY) || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!userId) return;
    const fetchFavorites = async () => {
      try {
        const res = await apiClient.get(`/api/admin/favorites/${userId}`);
        const data = (res.data || []).filter((f) => f.isFavourite === true);
        setFavorites(data);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
      } catch (err) {
        console.error("Failed to fetch favorites:", err);
      }
    };
    fetchFavorites();
  }, [userId]);

  const addFavorite = useCallback(async (menuName, displayName) => {
    if (favorites.some((f) => f.menuName === menuName)) return;

    const optimistic = { userId, menuName, displayName, isFavourite: true, id: `temp_${Date.now()}` };
    setFavorites((prev) => {
      const updated = [...prev, optimistic];
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(updated));
      return updated;
    });

    try {
      const res = await apiClient.post(`/api/admin/favorites`, {
        userId,
        menuName,
        displayName,
        isFavourite: true,
      });
      setFavorites((prev) => {
        const updated = prev.map((f) => f.id === optimistic.id ? { ...res.data, displayName } : f);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(updated));
        return updated;
      });
    } catch (err) {
      console.error("Failed to save favorite:", err);
      setFavorites((prev) => {
        const updated = prev.filter((f) => f.id !== optimistic.id);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(updated));
        return updated;
      });
    }
  }, [favorites, userId]);

  const removeFavorite = useCallback(async (menuName) => {
    const target = favorites.find((f) => f.menuName === menuName);
    if (!target) return;

    setFavorites((prev) => {
      const updated = prev.filter((f) => f.menuName !== menuName);
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(updated));
      return updated;
    });

    try {
      await apiClient.put(`/api/admin/favorites`, {
        userId,
        menuName,
        isFavourite: false,
      });
    } catch (err) {
      console.error("Failed to remove favorite:", err);
      setFavorites((prev) => {
        const updated = [...prev, target];
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(updated));
        return updated;
      });
    }
  }, [favorites, userId]);
  const isFavorite = useCallback(
    (menuName) => favorites.some((f) => f.menuName === menuName),
    [favorites]
  );
  return { favorites, addFavorite, removeFavorite, isFavorite };
};